import { Spin } from 'antd';
import useFetchEntities from 'hooks/useFetchEntities';
import useFetchEntity from 'hooks/useFetchEntity';
import { useEffect, useMemo, useRef } from 'react';
import PhaseSelector from './Filters/PhaseSelector';
import CompetitionLogo from './Parts/CompetitionLogo';
import IGImg from './Parts/IGImg';

import "@fontsource/audiowide";
import "@fontsource/bebas-neue";
import "@fontsource/covered-by-your-grace";
import "@fontsource/economica";
import "@fontsource/righteous";
import "@fontsource/russo-one";

import { Content } from 'antd/es/layout/layout';
import DebugPrinter from 'components/Debug/DebugPrinter';
import SizeSelector from 'components/Infographics/Filters/SizeSelector';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import usePhaseIdTranslator from 'hooks/usePhaseIdTranslator';
import usePersistentStore from 'store/persistentStore';
import { formatDateToLongFormat, formatTimeStringToHHMM } from 'utils/IGdateTimeFunctions';
import BackgroundSelector from './Filters/BackgroundSelector';
import DateRange from './Filters/DateRange';
import IgImgPlacement from './Filters/IgImgPlacement';
import IgImgSelector from './Filters/IgImgSelector';
import MarginEditor from './Filters/MarginEditor';
import VenueSelector from './Filters/VenueSelector';
import useZoomByMouseWheel from './Hooks/useZoomByMouseWheel';
import { createForegroundPositionExpression, createHandleFgImgMouseMove, dateFormatIG } from './IGconfig';
import GameCardTransparent from './Parts/GameCardTransparent';
import PeriodHeader from './Parts/HeaderPeriod';
dayjs.extend(customParseFormat);

const IG17_TopPlaysOfSelected = ({ showData, exportRef }) => {
  const membership = usePersistentStore((state) => state.get('ig-membership'));
  const setMembership = usePersistentStore((state) => state.set('ig-membership'));
  const venue = usePersistentStore((state) => state.get('ig-venue'));
  const setVenue = usePersistentStore((state) => state.set('ig-venue'));
  const fgPictureUrlA = usePersistentStore((state) => state.get('ig-topplays-fgA-url'));
  const setFgPictureUrlA = usePersistentStore((state) => state.set('ig-topplays-fgA-url'));
  const fgPictureUrlB = usePersistentStore((state) => state.get('ig-topplays-fgB-url'));
  const setFgPictureUrlB = usePersistentStore((state) => state.set('ig-topplays-fgB-url'));

  const dateRange = usePersistentStore((state) => state.get('ig-topplays-daterange'));
  const setDateRange = usePersistentStore((state) => state.set('ig-topplays-daterange'));

  const size = usePersistentStore((state) => state.get('ig-topplays-size'));
  const setSize = usePersistentStore((state) => state.set('ig-topplays-size'));
  const SIZE_PRESETS = ['PORTRAIT', 'CUSTOM'];
  const DEFAULT_SIZE = { width: 1100, height: 1955, preset: 'PORTRAIT' };

  const igname = 'topplays';

  const fgImgPositionAX = usePersistentStore((state) => state.get(`ig-${igname}-fgimgposition-a-x`)) ?? 0;
  const setFgImgPositionAX = usePersistentStore((state) => state.set(`ig-${igname}-fgimgposition-a-x`));
  const fgImgPositionAY = usePersistentStore((state) => state.get(`ig-${igname}-fgimgposition-a-y`)) ?? 0;
  const setFgImgPositionAY = usePersistentStore((state) => state.set(`ig-${igname}-fgimgposition-a-y`));
  const fgImgZoomA = usePersistentStore((state) => state.get(`ig-${igname}-fgzoom`)) ?? 450;
  const setFgImgZoomA = usePersistentStore((state) => state.set(`ig-${igname}-fgzoom`));
  const fgImgRefA = useRef(null);

  const fgImgPositionBX = usePersistentStore((state) => state.get(`ig-${igname}-fgimgposition-b-x`)) ?? 0;
  const setFgImgPositionBX = usePersistentStore((state) => state.set(`ig-${igname}-fgimgposition-b-x`));
  const fgImgPositionBY = usePersistentStore((state) => state.get(`ig-${igname}-fgimgposition-b-y`)) ?? 0;
  const setFgImgPositionBY = usePersistentStore((state) => state.set(`ig-${igname}-fgimgposition-b-y`));
  const fgImgZoomB = usePersistentStore((state) => state.get(`ig-${igname}-b-fgzoom`)) ?? 100;
  const setFgImgZoomB = usePersistentStore((state) => state.set(`ig-${igname}-b-fgzoom`));
  const fgImgRefB = useRef(null);

  const marginDefaults = { top: 32, left: 32, bottom: 32, right: 0 };
  const fgImgMargin = usePersistentStore((state) => state.get(`ig-${igname}-margin`)) ?? marginDefaults;
  const setFgImgMargin = usePersistentStore((state) => state.set(`ig-${igname}-margin`));

  // Background image and color
  const backgroundImgUrl = usePersistentStore((state) => state.get('ig-topplays-background-img-url'));
  const setBackgroundImgUrl = usePersistentStore((state) => state.set('ig-topplays-background-img-url'));
  const backgroundColor = usePersistentStore((state) => state.get('ig-topplays-background-img-color'));
  const setBackgroundColor = usePersistentStore((state) => state.set('ig-topplays-background-img-color'));
  const backgroundMode = usePersistentStore((state) => state.get('ig-topplays-background-mode'))
    ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  const setBackgroundMode = usePersistentStore((state) => state.set('ig-topplays-background-mode'));

  const groupId = membership?.groupId;
  const seasonId = membership?.seasonId;

  const { phaseIdsAll } = usePhaseIdTranslator(membership);

  const { data: groupDetail } = useFetchEntity('Group', groupId, 'Season>Competition');
  const { data: seasonDetail } = useFetchEntity('Season', seasonId, 'Competition');

  // Persist default date range if not already in store
  useEffect(() => {
    // console.log('dateRange', dateRange);
    if (!dateRange) {
      const defaultDateRange = [
        dayjs().startOf('month').format(dateFormatIG), 
        dayjs().endOf('month').format(dateFormatIG)
      ];
      setDateRange(defaultDateRange);
    }
  }, [dateRange, setDateRange]);

  useZoomByMouseWheel(fgImgRefA, fgImgZoomA, setFgImgZoomA);
  const handleFgImgAMouseMove = createHandleFgImgMouseMove(setFgImgPositionAX, setFgImgPositionAY);

  useZoomByMouseWheel(fgImgRefB, fgImgZoomB, setFgImgZoomB);
  const handleFgImgBMouseMove = createHandleFgImgMouseMove(setFgImgPositionBX, setFgImgPositionBY);


  /*
  
  https://api.dev.hms.wootera.net/admin/entities?entity=Game&
  relations=Venue(name),HomeTeam(nick,name,logoUrl),AwayTeam(nick,name,logoUrl),HomeTeamStars>Player(firstName,lastName),AwayTeamStars>Player(firstName,lastName),HomeTeamGoalies>Player(firstName,lastName),AwayTeamGoalies>Player(firstName,lastName),Phase(name),Phase>Group(name),Phase>Group>Season(name),Phase>Group>Season>Competition(name)
  &extraAttributes=HomeTeamGoals,AwayTeamGoals,HomeTeamPenalties,AwayTeamPenalties,HomeTeamPenaltyMinutes,AwayTeamPenaltyMinutes,HomeTeamSaves,AwayTeamSaves,HomeTeamFaceOffs,AwayTeamFaceOffs
  &onlyAttributes=gameId,startDate,startTime,overwriteEnabled,timekeeper,referee,name
  &search=&organizationId="BB669217-41EB-4162-8040-A73F49A43570"
   
   */

  const venueIdsAll = venue?.venueIds == null || venue?.venueIds.length === 0 ? undefined : venue?.venueIds;

  const { data: gamesAllInclStars, isLoading } = useFetchEntities(
    (dateRange != null && dateRange[0] != null && dateRange[1] != null) && phaseIdsAll != null && 'Game',
    {
      filters: {
        phaseId: phaseIdsAll,
        venueId: venueIdsAll,
        startDate: (dateRange != null && dateRange[0] != null && dateRange[1] != null) ?
          [`>=${dateRange[0]}`, `<=${dateRange[1]}`] : undefined
      },
      // Venue(name, logoUrl)
      relations: 'Venue(name,logoUrl),HomeTeam(nick,name,logoUrl),AwayTeam(nick,name,logoUrl),HomeTeamStars>Player(firstName,lastName),AwayTeamStars>Player(firstName,lastName),HomeTeamGoalies>Player(firstName,lastName),AwayTeamGoalies>Player(firstName,lastName),Phase(name),Phase>Group(name,color),Phase>Group>Season(name),Phase>Group>Season>Competition(name)',
      extraAttributes: 'HomeTeamGoals,AwayTeamGoals,HomeTeamPenalties,AwayTeamPenalties,HomeTeamPenaltyMinutes,AwayTeamPenaltyMinutes,HomeTeamSaves,AwayTeamSaves,HomeTeamFaceOffs,AwayTeamFaceOffs',
      onlyAttributes: 'gameId,startDate,startTime,overwriteEnabled,timekeeper,referee,name'
    }
  );

  // prepare metadata

  const metadata =
    useMemo(() => {
      return {
        GroupName: groupDetail?.name,
        GroupColor: groupDetail?.color,
        SeasonName: seasonDetail?.name,
        CompetitionName: seasonDetail?.Competition?.name,
        CompetitionLogo:
          seasonDetail?.Competition?.logoUrl?.replace(
            "[size]",
            "cropped_md",
          ),
        Date: dateRange?.[0] //dayjs().format(dateFormatIG), //TODO
      };
    }, [groupDetail, seasonDetail, dateRange]);

  // prepare data

  const calculateTopnessScore1 = (game) => {
    const aMaxGoals = Math.max(game.HomeTeamGoals, game.AwayTeamGoals);
    const aMinGoals = Math.min(game.HomeTeamGoals, game.AwayTeamGoals);
    return aMaxGoals - aMinGoals;
  }

  const calculateTopnessScore2 = (game) => {
    return game.HomeTeamGoals + game.AwayTeamGoals;
  }

  const data = useMemo(() => {
    return gamesAllInclStars
      ?.map((g) => ({
        VenueLogo: g.Venue?.logoUrl?.replace("[size]", "cropped_md"),
        HomeTeamLogo: g.HomeTeam?.logoUrl?.replace("[size]", "cropped_md"),
        AwayTeamLogo: g.AwayTeam?.logoUrl?.replace("[size]", "cropped_md"),
        Score: `${g.HomeTeamGoals}:${g.AwayTeamGoals}`,
        Date: g.startDate,
        Time: g.startTime,
        HomeTeamStarFirstName: g.HomeTeamStars?.[0]?.Player?.firstName,
        HomeTeamStarLastName: g.HomeTeamStars?.[0]?.Player?.lastName,
        AwayTeamStarFirstName: g.AwayTeamStars?.[0]?.Player?.firstName,
        AwayTeamStarLastName: g.AwayTeamStars?.[0]?.Player?.lastName,
        TopnessScore1: calculateTopnessScore1(g),
        TopnessScore2: calculateTopnessScore2(g),
      }))
      .sort((a, b) => {
        const ts1Diff = b.TopnessScore1 - a.TopnessScore1;
        if (ts1Diff !== 0) {
          return ts1Diff;
        }
        const ts2Diff = b.TopnessScore2 - a.TopnessScore2;
        if (ts2Diff !== 0) {
          return ts2Diff;
        }
        return dayjs(a.startDate).isAfter(b.startDate) ? -1 : 1;
      })
      .slice(0, 10);
  }, [gamesAllInclStars]);


  return (
    <>
      <div className="grid grid-cols-[auto,1fr] gap-3 items-center">

        <span>Membership:</span>
        <PhaseSelector
          value={membership}
          onChange={setMembership}
          mandatory={['competitionId', 'seasonId']}
        />

        <span>Choose period:</span>
        <div>
          <DateRange
            onChange={(_, dateStrings) => { setDateRange(dateStrings) }}
            value={[dayjs(dateRange?.[0], dateFormatIG), dayjs(dateRange?.[1], dateFormatIG)]}>
          </DateRange>
        </div>

        <span>Foreground image [right]:</span>
        <div>
          <IgImgSelector fgPictureUrl={fgPictureUrlA} setFgPictureUrl={setFgPictureUrlA} igId={17} />
        </div>

        <span className="relative top-[-14px]">Foreground placement [right]:</span>
        <div >
          <IgImgPlacement
            fgImgPositionX={fgImgPositionAX}
            fgImgPositionY={fgImgPositionAY}
            setFgImgPositionX={setFgImgPositionAX}
            setFgImgPositionY={setFgImgPositionAY}
            fgImgZoom={fgImgZoomA}
            setFgImgZoom={setFgImgZoomA}
          />
        </div>

        <span>Foreground image [bottom]:</span>
        <div>
          <IgImgSelector fgPictureUrl={fgPictureUrlB} setFgPictureUrl={setFgPictureUrlB} igId={'17b'} />
        </div>

        <span className="relative top-[-14px]">Foreground placement [bottom]:</span>
        <div >
          <IgImgPlacement
            fgImgPositionX={fgImgPositionBX}
            fgImgPositionY={fgImgPositionBY}
            setFgImgPositionX={setFgImgPositionBX}
            setFgImgPositionY={setFgImgPositionBY}
            fgImgZoom={fgImgZoomB}
            setFgImgZoom={setFgImgZoomB}
          />
        </div>

        <span>Choose venue:</span>
        <div>
          <VenueSelector
            value={venue}
            onChange={(venue) => { setVenue(venue) }}
          />
        </div>

        <span>Background:</span>
        <BackgroundSelector
          backgroundMode={backgroundMode}
          setBackgroundMode={setBackgroundMode}
          backgroundImgUrl={backgroundImgUrl}
          setBackgroundImgUrl={setBackgroundImgUrl}
          backgroundColor={backgroundColor}
          setBackgroundColor={setBackgroundColor}
          igId={17}
        />

        <span>Dimensions:</span>
        <div className="flex gap-2 items-center">
          <SizeSelector
            value={size}
            onChange={setSize}
            presets={SIZE_PRESETS}
            defaultValue={DEFAULT_SIZE}
          />
        </div>

        <span>Margin:</span>
        <div className="flex gap-2 items-center">
          <MarginEditor
            margin={fgImgMargin}
            setMargin={setFgImgMargin}
            defaults={marginDefaults}
          />
        </div>

        <div />
      </div>

      {isLoading &&
        <Content className="flex items-center justify-center">
          <Spin />
        </Content>
      }

      {showData && (<DebugPrinter data={[metadata, data]}></DebugPrinter>)}

      {phaseIdsAll && gamesAllInclStars &&
        <article
          ref={exportRef}
          style={{
            width: `${size?.width}px`,
            height: `${size?.height}px`,
            ...(backgroundMode === 'COLOR' && backgroundColor
              ? { backgroundColor }
              : backgroundMode === 'IMAGE' && backgroundImgUrl
                ? {
                    backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }
                : {
                    backgroundColor: '#323f50',
                  }),
          }}
          className="relative mx-auto min-h-[1000px]  w-[800px] overflow-hidden"
        >
          <div 
            className="relative h-full grid grid-cols-5 font-header text-white z-[2]"
            style={{
              ...(backgroundMode === 'COLOR' && backgroundColor
                ? { backgroundColor }
                : backgroundMode === 'IMAGE' && backgroundImgUrl
                  ? {
                      backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }
                  : {
                      backgroundColor: '#323f50',
                    }),
            }}
          >
            <div className="col-span-3 flex flex-col"
              style={{
                paddingTop: `${fgImgMargin.top}px`,
                paddingLeft: `${fgImgMargin.left}px`,
                paddingRight: `${fgImgMargin.right}px`,
                paddingBottom: `${fgImgMargin.bottom}px`,
              }}>
              {/* TODO: Replace title string with a variable */}
              <PeriodHeader
                className="border-b-2 border-white"
                title="Top plays of the period"
                startDate={dateRange?.[0]}
                endDate={dateRange?.[1]}
                league={metadata.GroupName} //"KLASIK divize"
                leagueStyle={{ color: metadata.GroupColor ?? '#777777' }}
              //leagueClassName="text-orange-400"
              />

              <div className="flex h-full flex-col items-center justify-between">
                <div className="flex flex-col gap-2">
                  {data?.map(
                    (game, index) =>
                      index < 10 && (
                        <GameCardTransparent
                          innerHorizontalWidth={'w-[128px]'}
                          key={index}
                          game={game}
                          textUnder={`${formatDateToLongFormat(game.Date)}, ${formatTimeStringToHHMM(game.Time)}, Stars: ${game.HomeTeamStarFirstName ?? '-'} ${game.HomeTeamStarLastName ?? ''}, ${game.AwayTeamStarFirstName ?? '-'} ${game.AwayTeamStarLastName ?? ''}`}
                        >
                          {game.Score}
                        </GameCardTransparent>
                      ),
                  )}
                </div>
                <CompetitionLogo
                  className="mb-8 mt-12"
                  src={metadata.CompetitionLogo}
                  title={metadata.CompetitionName}
                  season={metadata.SeasonName}
                />
              </div>
            </div>
            <div
              ref={fgImgRefA}
              onMouseMove={handleFgImgAMouseMove}
              className='w-full relative col-span-2 h-full'
              style={{
                backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${fgPictureUrlA}')`,
                backgroundSize: `${fgImgZoomA ?? 100}%`, // 'cover'
                backgroundPosition: createForegroundPositionExpression(fgImgPositionAX, fgImgPositionAY),
                backgroundRepeat: 'no-repeat',
                cursor: 'grab', // Visual cue for draggable behavior
                clipPath: "polygon(50px 0, 100% 0, 100% 100%, 0% 100%)",
              }}
              onMouseDown={(e) => e.target.style.cursor = 'grabbing'} // Change cursor while dragging
              onMouseUp={(e) => e.target.style.cursor = 'grab'} // Reset cursor
            ></div>

            <IGImg
              src="/images/highest-wins-month.png"
              className={`absolute -right-4 z-20 h-auto w-[650px]`}
              style={{ bottom: `${-54 + fgImgMargin.bottom}px` }}
              alt=""
            />

          </div>
          {/* Absolute images behind */}

          <div
            ref={fgImgRefB}
            onMouseMove={handleFgImgBMouseMove}
            className="absolute bottom-0 h-[40%] w-full"
            style={{
              backgroundImage: `url('${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${fgPictureUrlB}')`,
              backgroundSize: `${fgImgZoomB ?? 100}%`, // 'cover'
              backgroundPosition: createForegroundPositionExpression(fgImgPositionBX, fgImgPositionBY),
              backgroundRepeat: 'no-repeat',
              cursor: 'grab', // Visual cue for draggable behavior
            }}
            onMouseDown={(e) => e.target.style.cursor = 'grabbing'} // Change cursor while dragging
            onMouseUp={(e) => e.target.style.cursor = 'grab'} // Reset cursor
          ></div>
        </article>}
    </>

  );
};

export default IG17_TopPlaysOfSelected;
